import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ChevronLeft, X, ChevronRight, MessageSquare, ArrowUp, Paperclip, Mic } from "lucide-react";
import { useDispatch } from "react-redux";
import axios from "axios";
import DOMPurify from "dompurify";
import { getSocket } from "../utils/useChatSocket";
import {
  getValidDexySession,
  saveDexySession,
  getValidChatSession,
  getUnreadThreads,
  clearUnreadThread,
  addKnownRoom
} from "../utils/sessionManager";
import { createDexyConvo, setConvo } from "../Redux/Actions/convoActions";
import { getAgentInfoById, getCachedAgentInfo } from "../utils/agentService";
import FeedbackForm from "./FeedbackForm";
import Feedback from "./Feedback";
import "../CSS/Message.css";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const initialToken = localStorage.getItem("dexkor-chat-widget-auth-token");
const socket = getSocket(initialToken);

// Gleap-style Dexy Bot Avatar: black circle with 3 horizontal white dots
const DexyBotIcon = ({ className = "w-5 h-5", dotClass = "w-1 h-1", gap = "gap-[2px]" }) => (
  <div className={`${className} rounded-full bg-black flex items-center justify-center text-white shrink-0 shadow-sm`}>
    <div className={`flex items-center ${gap}`}>
      <div className={`${dotClass} rounded-full bg-white`} />
      <div className={`${dotClass} rounded-full bg-white`} />
      <div className={`${dotClass} rounded-full bg-white`} />
    </div>
  </div>
);

const DexyAi = ({
  setView,
  setupDetails,
  widgetConfigs,
  setActiveIcon,
  setIsOpen,
  initialMessage,
  onMessageConsumed,
  targetRoomId,
  onRoomConsumed,
  conversations = [],
  fetchConversations,
  customerDetails,
}) => {
  const dispatch = useDispatch();
  const [userInput, setUserInput] = useState("");
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const initialSentRef = useRef(false);
  const activeIntervalRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Derive theme/UI colors matching the widget setup
  const uiColor =
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    setupDetails?.widgetCustomizations?.primaryColor ||
    "#1C243A";

  const color1 =
    setupDetails?.widgetCustomizations?.color1 ||
    setupDetails?.widgetCustomizations?.primaryColor ||
    "#4c73de";

  const color2 =
    setupDetails?.widgetCustomizations?.color2 ||
    setupDetails?.widgetCustomizations?.secondaryColor ||
    "#2a4dad";

  const botName = setupDetails?.widgetCustomizations?.aiBotName || "Dexy";
  const brandName =
    setupDetails?.partnerDetails?.brandName ||
    setupDetails?.widgetCustomizations?.partnerName ||
    "";

  // Screen state: "list" (previous messages view) or "chat" (active chat room view)
  const [activeScreen, setActiveScreen] = useState(() => {
    if (initialMessage) return "chat";
    return "list";
  });

  // Dedicated Conversation state
  const [dexyConvo, setDexyConvo] = useState(() => {
    const session =
      getValidDexySession(widgetConfigs?.uniquePartnerId) ||
      getValidChatSession(widgetConfigs?.uniquePartnerId);
    return session?.conversation || null;
  });

  const [customerConversations, setCustomerConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Unread thread rooms tracking
  const [unreadThreadIds, setUnreadThreadIds] = useState(() =>
    getUnreadThreads(widgetConfigs?.uniquePartnerId)
  );

  useEffect(() => {
    const handleUnreadUpdate = () => {
      setUnreadThreadIds(getUnreadThreads(widgetConfigs?.uniquePartnerId));
    };
    window.addEventListener("dexkor:unread_thread_updated", handleUnreadUpdate);
    return () => {
      window.removeEventListener("dexkor:unread_thread_updated", handleUnreadUpdate);
    };
  }, [widgetConfigs?.uniquePartnerId]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [thinkingStage, setThinkingStage] = useState(0); // 0 = Thinking..., 1 = Gathering information...
  const [handedOff, setHandedOff] = useState(dexyConvo?.botHandled === "handed_off");
  const [agentProfiles, setAgentProfiles] = useState({});
  const [availableAgents, setAvailableAgents] = useState([]);

  // Word-by-word streaming effect state for AI responses
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamedTextMap, setStreamedTextMap] = useState({});
  const [feedbackState, setFeedbackState] = useState({});

  // Cycle thinking stage when loading is active (Thinking -> Gathering information...)
  useEffect(() => {
    let timer = null;
    if (loading) {
      setThinkingStage(0);
      timer = setTimeout(() => {
        setThinkingStage(1);
      }, 1500);
    } else {
      setThinkingStage(0);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading]);

  // Fetch real available agents whose status is "Active" (UserSchema.js:L7 availabilityStatus) from auth microservice
  useEffect(() => {
    const partnerId =
      widgetConfigs?.uniquePartnerId ||
      localStorage.getItem("uniquePartnerId") ||
      localStorage.getItem("partner_id") ||
      "66f7d1662ea86574e070c536";

    if (!partnerId) return;

    const fetchAgents = async () => {
      try {
        const authUrl =
          process.env.REACT_APP_AUTH_MICROSERVICE_URL ||
          "https://auth-stg.dexkor.com/api/v1";
        const res = await axios.get(`${authUrl}/users/all/${partnerId}`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          // Filter agents whose status is "Active" as per UserSchema.js:L7 availabilityStatus enum: ["Active", "Idle", "Away"]
          const activeUsers = res.data.filter((u) => {
            const availStatus = u?.availabilityStatus?.status;
            const isAvailActive = availStatus === "Active";
            const isAccountActive = u?.status === "Active" || !u?.status;
            const isAvailable = u?.available !== false;
            const hasName = Boolean(u?.name && u.name.trim());

            return isAvailActive && isAccountActive && isAvailable && hasName;
          });

          // Deduplicate distinct active users
          const seenNames = new Set();
          const uniqueActive = [];
          activeUsers.forEach((u) => {
            const cleanName = u.name.replace(/\(.*?\)/g, "").trim().toLowerCase();
            if (!seenNames.has(cleanName)) {
              seenNames.add(cleanName);
              uniqueActive.push(u);
            }
          });

          // Sort: prioritize users with real profile pictures first
          const sorted = [...uniqueActive].sort((a, b) => {
            const aImg = Boolean(a.image || a.profilePic || a.profilePicture || a.avatar);
            const bImg = Boolean(b.image || b.profilePic || b.profilePicture || b.avatar);
            if (aImg && !bImg) return -1;
            if (!aImg && bImg) return 1;
            return 0;
          });

          const mapped = sorted.slice(0, 3).map((u) => ({
            id: u._id,
            name: u.name,
            avatar: u.image || u.profilePic || u.profilePicture || u.avatar || null,
            initials: u.name
              .split(" ")
              .filter(Boolean)
              .map((p) => p[0])
              .join("")
              .toUpperCase()
              .substring(0, 2),
          }));

          if (mapped.length > 0) {
            setAvailableAgents(mapped);
          }
        }
      } catch (err) {
        console.log("Agents fetch note:", err.message);
      }
    };

    fetchAgents();
  }, [widgetConfigs?.uniquePartnerId]);

  // Helper: check if a conversation belongs strictly to widget convo (source: widget, subSource: convo)
  const isWidgetConvo = (c) => {
    const ticket = c?.ticketId;
    if (ticket && typeof ticket === "object") {
      const isConvoSource = ticket.source === "widget" && ticket.subSource === "convo";
      const isConvoCustom =
        ticket.customFields?.isDexyAiConvo === true ||
        ticket.customFields?.convoType === "dexy_ai";
      return isConvoSource || isConvoCustom;
    }
    if (c?.source === "widget" && c?.subSource === "convo") return true;
    if (c?.source === "dexy_ai") return true;
    if (c?.roomId && String(c.roomId).startsWith("DexyRoom-")) return true;
    return false;
  };

  // Helper: Strip HTML tags and decode entities (e.g. &nbsp;) to clean text
  const cleanTextSnippet = (rawText) => {
    if (!rawText || typeof rawText !== "string") return "";

    let text = rawText;
    text = text.replace(/<!--[\s\S]*?-->/g, "");
    text = text.replace(/<[^>]*>/g, " ");

    text = text
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&#x2F;/gi, "/")
      .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
      .replace(/&[a-z0-9#]+;/gi, " ");

    text = text.replace(/[*_#`~[\]()]/g, "");
    return text.replace(/\s+/g, " ").trim();
  };

  // Fetch previous conversations for the customer by email, phone, or customerId (source: widget, subSource: convo only)
  const fetchCustomerConversations = useCallback(async () => {
    const partnerId =
      widgetConfigs?.uniquePartnerId ||
      localStorage.getItem("uniquePartnerId") ||
      localStorage.getItem("partner_id");

    if (!partnerId) return;

    let storedCustomer = {};
    try {
      storedCustomer =
        JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details")) || {};
    } catch (e) { }

    const email =
      storedCustomer.email ||
      customerDetails?.email ||
      widgetConfigs?.userEmail ||
      "";
    const phone =
      storedCustomer.phone ||
      storedCustomer.number ||
      customerDetails?.phone ||
      customerDetails?.number ||
      widgetConfigs?.userNumber ||
      widgetConfigs?.userPhone ||
      "";
    const customerId =
      storedCustomer._id ||
      customerDetails?._id ||
      widgetConfigs?.userId ||
      "";

    setLoadingHistory(true);
    try {
      let fetchedList = [];

      // 1. Try our dedicated user-history endpoint on chat-server
      if (email || phone || customerId) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/conversations/user-history/${partnerId}`,
            {
              params: {
                email: email || undefined,
                phone: phone || undefined,
                customerId: customerId || undefined,
              },
            }
          );
          if (Array.isArray(res.data) && res.data.length > 0) {
            fetchedList = res.data;
          }
        } catch (err) {
          console.log("Note: user-history query:", err.message);
        }
      }

      // 2. If empty and customerId exists, try standard /conversations/:customerId/:partnerId
      if (fetchedList.length === 0 && customerId) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/conversations/${customerId}/${partnerId}`
          );
          if (Array.isArray(res.data) && res.data.length > 0) {
            fetchedList = res.data;
          }
        } catch (err) {
          console.log("Note: customerId conversation query:", err.message);
        }
      }

      // 3. Fallback to passed conversations prop if provided
      if (fetchedList.length === 0 && Array.isArray(conversations) && conversations.length > 0) {
        fetchedList = conversations;
      }

      // 4. Merge active 24-hr session from localStorage if present
      const activeSession =
        getValidDexySession(partnerId) || getValidChatSession(partnerId);
      if (activeSession && activeSession.conversation) {
        const activeConvo = activeSession.conversation;
        const exists = fetchedList.some(
          (c) =>
            (c.roomId && activeConvo.roomId && c.roomId === activeConvo.roomId) ||
            (c._id && activeConvo._id && String(c._id) === String(activeConvo._id))
        );
        if (!exists) {
          fetchedList = [activeConvo, ...fetchedList];
        }
      }

      // Filter: only conversations that have roomId AND match source: widget, subSource: convo
      const validConvos = fetchedList.filter((c) => Boolean(c?.roomId) && isWidgetConvo(c));

      // Sort newest first by last activity
      validConvos.sort((a, b) => {
        const timeA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });

      // Register all room IDs to known rooms and socket room subscriptions
      validConvos.forEach((c) => {
        if (c.roomId) addKnownRoom(widgetConfigs?.uniquePartnerId, c.roomId);
      });
      const roomIds = validConvos.map((c) => c.roomId).filter(Boolean);
      if (roomIds.length > 0) {
        socket.emit("join", roomIds);
      }

      setCustomerConversations(validConvos);
    } catch (error) {
      console.error("Error loading previous conversations in DexyAi:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [widgetConfigs, conversations, customerDetails]);

  // Load previous conversations on mount or when props update
  useEffect(() => {
    fetchCustomerConversations();
  }, [fetchCustomerConversations]);

  // Fetch agent profiles for any conversations that have an Agent senderId
  useEffect(() => {
    customerConversations.forEach((conv) => {
      const msgs = Array.isArray(conv.messages) ? conv.messages : [];
      const lastAgentMsg = [...msgs].reverse().find((m) => m.sender === "Agent" && m.senderId);
      const agentSenderId = lastAgentMsg?.senderId || (conv.userId ? String(conv.userId) : null);
      if (agentSenderId && !agentProfiles[agentSenderId]) {
        const cached = getCachedAgentInfo(agentSenderId);
        if (cached) {
          setAgentProfiles((prev) => ({ ...prev, [agentSenderId]: cached }));
        } else {
          getAgentInfoById(agentSenderId).then((info) => {
            if (info) {
              setAgentProfiles((prev) => ({ ...prev, [agentSenderId]: info }));
            }
          });
        }
      }
    });
  }, [customerConversations, agentProfiles]);

  // Fetch agent profiles for active chat messages or assigned agent
  useEffect(() => {
    messages.forEach((m) => {
      if (m.sender === "Agent" && m.senderId && !agentProfiles[m.senderId]) {
        const cached = getCachedAgentInfo(m.senderId);
        if (cached) {
          setAgentProfiles((prev) => ({ ...prev, [m.senderId]: cached }));
        } else {
          getAgentInfoById(m.senderId).then((info) => {
            if (info) {
              setAgentProfiles((prev) => ({ ...prev, [m.senderId]: info }));
            }
          });
        }
      }
    });

    const convAgentId = dexyConvo?.userId || dexyConvo?.assignee;
    if (convAgentId) {
      const id = typeof convAgentId === "object" ? String(convAgentId._id || "") : String(convAgentId);
      if (id && !agentProfiles[id]) {
        getAgentInfoById(id).then((info) => {
          if (info) setAgentProfiles((prev) => ({ ...prev, [id]: info }));
        });
      }
    }
  }, [messages, dexyConvo?.userId, dexyConvo?.assignee, agentProfiles]);

  // Sync / join room and fetch messages when dexyConvo becomes available
  useEffect(() => {
    if (!dexyConvo?.roomId) return;

    socket.emit("join", [dexyConvo.roomId]);

    // If dexyConvo already has messages, seed them into state
    if (Array.isArray(dexyConvo.messages) && dexyConvo.messages.length > 0) {
      setMessages((prev) => {
        if (prev.length === 0) return dexyConvo.messages;
        const map = new Map();
        [...dexyConvo.messages, ...prev].forEach((m) => {
          const key = m._id || m.clientMessageId || `${m.sender}_${m.message}_${m.timestamp}`;
          if (!map.has(key)) map.set(key, m);
        });
        return Array.from(map.values()).sort((a, b) => {
          const timeA = new Date(a.timestamp || a.createdAt || 0).getTime();
          const timeB = new Date(b.timestamp || b.createdAt || 0).getTime();
          return timeA - timeB;
        });
      });
    }

    // Fetch latest messages from database for this room
    const fetchUrl = `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/conversations/room/${dexyConvo.roomId}`;
    axios
      .get(fetchUrl)
      .then((res) => {
        if (res.data?.conversation?.botHandled === "handed_off") setHandedOff(true);
        if (res.data?.success && Array.isArray(res.data.messages) && res.data.messages.length > 0) {
          setMessages((prev) => {
            const map = new Map();
            [...prev, ...res.data.messages].forEach((m) => {
              const key = m._id || m.clientMessageId || `${m.sender}_${m.message}_${m.timestamp}`;
              if (!map.has(key)) map.set(key, m);
            });
            return Array.from(map.values()).sort((a, b) => {
              const timeA = new Date(a.timestamp || a.createdAt || 0).getTime();
              const timeB = new Date(b.timestamp || b.createdAt || 0).getTime();
              return timeA - timeB;
            });
          });
        }
      })
      .catch((err) => {
        console.log("Dexy conversation fetch note:", err.message);
      });
  }, [dexyConvo?.roomId]);

  // Handle incoming socket messages & typewriter word-by-word streaming effect for bot
  useEffect(() => {
    if (!dexyConvo?.roomId) return;

    const handleRecv = (msgData) => {
      if (!msgData) return;
      const targetRoom = msgData.room || msgData.roomId;
      if (targetRoom !== dexyConvo.roomId) return;

      const stableMsgId = msgData._id || msgData.clientMessageId || `bot_msg_${Date.now()}`;
      msgData._id = stableMsgId;

      if (msgData.sender === "Agent" && msgData.senderId && !agentProfiles[msgData.senderId]) {
        getAgentInfoById(msgData.senderId).then((info) => {
          if (info) setAgentProfiles((prev) => ({ ...prev, [msgData.senderId]: info }));
        });
      }

      setMessages((prev) => {
        if (msgData._id && prev.some((m) => m._id && String(m._id) === String(msgData._id))) {
          return prev;
        }

        // 2. Exact clientMessageId match (update optimistic message with server data)
        if (
          msgData.clientMessageId &&
          prev.some((m) => m.clientMessageId && m.clientMessageId === msgData.clientMessageId)
        ) {
          return prev.map((m) => {
            if (m.clientMessageId && m.clientMessageId === msgData.clientMessageId) {
              return { ...m, ...msgData };
            }
            return m;
          });
        }

        const incomingTime = new Date(msgData.timestamp || msgData.createdAt || Date.now()).getTime();
        const existingIndex = prev.findIndex((m) => {
          if (m.sender !== msgData.sender) return false;
          if (m.message !== msgData.message) return false;
          const mTime = new Date(m.timestamp || m.createdAt || Date.now()).getTime();
          return Math.abs(incomingTime - mTime) < 10000;
        });

        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...msgData };
          return updated;
        }

        // 4. Truly new message
        return [...prev, msgData];
      });

      // If it's a Bot / AI answer, trigger typewriter streaming effect
      const isBotAnswer =
        msgData.sender === "Chatbot" ||
        msgData.sender === "Bot" ||
        msgData.fromAddress === "Dexy AI" ||
        msgData.fromAddress === botName ||
        msgData.fromAddress === "Dexy";

      if (isBotAnswer && msgData.message) {
        setLoading(false);
        const rawContent = msgData.message.trim();
        const words = rawContent.split(/\s+/);

        if (words.length > 1) {
          if (activeIntervalRef.current) {
            clearInterval(activeIntervalRef.current);
            activeIntervalRef.current = null;
          }

          setStreamingMessageId(stableMsgId);
          let currentWordIndex = 0;
          setStreamedTextMap((prev) => ({ ...prev, [stableMsgId]: words[0] }));

          activeIntervalRef.current = setInterval(() => {
            currentWordIndex += 1;
            if (currentWordIndex < words.length) {
              setStreamedTextMap((prev) => ({
                ...prev,
                [stableMsgId]: words.slice(0, currentWordIndex + 1).join(" "),
              }));
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
              }
            } else {
              clearInterval(activeIntervalRef.current);
              activeIntervalRef.current = null;
              setStreamingMessageId(null);
              setStreamedTextMap((prev) => ({ ...prev, [stableMsgId]: rawContent }));
            }
          }, 30);
        } else {
          setStreamedTextMap((prev) => ({ ...prev, [stableMsgId]: rawContent }));
        }
      } else if (msgData.sender === "Agent") {
        setLoading(false);
      }
    };

    socket.on("message", handleRecv);
    socket.on("receiveMessage", handleRecv);

    const onBotStatus = (data) => {
      if (data.roomId === dexyConvo?.roomId && data.botHandled === "handed_off") {
        setHandedOff(true);
        setLoading(false);
      }
    };
    socket.on("conversation:botStatus", onBotStatus);

    return () => {
      if (activeIntervalRef.current) {
        clearInterval(activeIntervalRef.current);
        activeIntervalRef.current = null;
      }
      socket.off("message", handleRecv);
      socket.off("receiveMessage", handleRecv);
      socket.off("conversation:botStatus", onBotStatus);
    };
  }, [dexyConvo?.roomId, agentProfiles, botName]);

  const handleSendMessage = async (textOverride, interactiveReplyId = null) => {
    const text = (typeof textOverride === "string" ? textOverride : userInput).trim();
    if (!text) return;

    if (typeof textOverride !== "string") {
      setUserInput("");
    }

    let activeConvo = dexyConvo;
    if (!activeConvo) {
      setLoading(true);
      activeConvo = await dispatch(createDexyConvo(widgetConfigs));
      if (activeConvo) {
        setDexyConvo(activeConvo);
      }
    }

    if (!activeConvo?.roomId) {
      setLoading(false);
      return;
    }

    if (!isAgentHandling) {
      setLoading(true);
    }

    const clientMsgId = `client_${Date.now()}_${Math.random()}`;
    const userMsgObj = {
      message: text,
      roomId: activeConvo.roomId,
      room: activeConvo.roomId,
      sender: "Customer",
      interactiveReplyId,
      metadata: { supportsButtons: true },
      clientMessageId: clientMsgId,
      timestamp: Date.now(),
      type: "External",
      senderId: JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details"))?._id || "",
      uniquePartnerId:
        activeConvo.uniquePartnerId ||
        widgetConfigs?.uniquePartnerId ||
        localStorage.getItem("partner_id"),
    };

    // Optimistically add user's message to the chat
    setMessages((prev) => [...prev, userMsgObj]);

    socket.emit("message", userMsgObj);
  };

  // Auto-send initial message passed from home page once in chat mode
  useEffect(() => {
    if (initialMessage && dexyConvo && dexyConvo.roomId && !initialSentRef.current) {
      initialSentRef.current = true;
      setActiveScreen("chat");
      handleSendMessage(initialMessage);
      if (onMessageConsumed) {
        onMessageConsumed();
      }
    }
  }, [dexyConvo, initialMessage]);

  // Robust auto-scroll to bottom using scrollIntoView
  const scrollToBottom = useCallback((instant = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: instant ? "auto" : "smooth",
        block: "end",
      });
    } else if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  // When switching to chat screen or changing conversations, scroll to bottom
  useEffect(() => {
    if (activeScreen === "chat") {
      const timer = setTimeout(() => {
        scrollToBottom(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activeScreen, dexyConvo?.roomId, scrollToBottom]);

  // When messages or loading change, scroll to bottom
  useEffect(() => {
    if (activeScreen === "chat" && messages.length > 0) {
      const timer = setTimeout(() => {
        scrollToBottom(false);
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [messages.length, loading, activeScreen, scrollToBottom]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Speech-to-text toggle
  const toggleListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (isListening) {
      if (recognitionRef.current) recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => setIsListening(true);
      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          transcript += event.results[i][0].transcript;
        }
        setUserInput((prev) => (prev ? prev + " " + transcript : transcript));
      };
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error(err);
      setIsListening(false);
    }
  };

  // Select an existing previous conversation to continue it
  const handleSelectConversation = useCallback((conv) => {
    const isBuilderMode = setupDetails?.widgetWorkflow?.enabled && !['legacy', 'dexy_ai'].includes(setupDetails?.widgetWorkflow?.workflowId);
    if (isBuilderMode) {
      dispatch(setConvo(conv));
      setView("AutomatedBot");
      return;
    }
    setDexyConvo(conv);
    setHandedOff(conv.botHandled === "handed_off");
    setLoading(false);
    setActiveScreen("chat");

    if (conv?.roomId) {
      clearUnreadThread(widgetConfigs?.uniquePartnerId, conv.roomId);
      setUnreadThreadIds((prev) => prev.filter((id) => id !== conv.roomId));
      socket.emit("join", [conv.roomId]);
      saveDexySession(widgetConfigs?.uniquePartnerId, conv);
      addKnownRoom(widgetConfigs?.uniquePartnerId, conv.roomId);
    }

    if (Array.isArray(conv.messages) && conv.messages.length > 0) {
      setMessages(conv.messages);
    }
  }, [widgetConfigs?.uniquePartnerId]);

  // Handle redirect directly to a specific target room (e.g. from closed widget notification click)
  useEffect(() => {
    if (!targetRoomId) return;
    const found = customerConversations.find(
      (c) => c.roomId === targetRoomId || c._id === targetRoomId
    );
    if (found) {
      handleSelectConversation(found);
    } else {
      handleSelectConversation({ roomId: targetRoomId });
    }
    clearUnreadThread(widgetConfigs?.uniquePartnerId, targetRoomId);
    setUnreadThreadIds((prev) => prev.filter((id) => id !== targetRoomId));
    if (onRoomConsumed) onRoomConsumed();
  }, [targetRoomId, customerConversations, handleSelectConversation, onRoomConsumed, widgetConfigs?.uniquePartnerId]);

  // Start a new conversation flow when "Send us a message" is clicked (showing 3 overlapping agents in header during session init)
  const handleStartNewChat = async () => {
    const isBuilderMode = setupDetails?.widgetWorkflow?.enabled && !['legacy', 'dexy_ai'].includes(setupDetails?.widgetWorkflow?.workflowId);
    if (isBuilderMode) {
      setView("AutomatedBot");
      return;
    }
    setMessages([]);
    setHandedOff(false);
    setLoading(false);
    setUserInput("");
    setInitializing(true);
    setActiveScreen("chat");

    const startTime = Date.now();
    try {
      const newConvo = await dispatch(createDexyConvo(widgetConfigs, { forceNew: true }));
      if (newConvo) {
        setDexyConvo(newConvo);
        if (newConvo.roomId) {
          socket.emit("join", [newConvo.roomId]);
        }
      }
    } catch (err) {
      console.error("Error creating new conversation:", err);
    } finally {
      // Keep session creation state visible for ~2.6s (Image 1 style)
      const elapsed = Date.now() - startTime;
      const delay = Math.max(2600 - elapsed, 1000);
      setTimeout(() => {
        setInitializing(false);
      }, delay);
    }
  };

  const handleBackFromChat = () => {
    setActiveScreen("list");
    fetchCustomerConversations();
  };

  const handleBackFromList = () => {
    const cust = setupDetails?.widgetCustomizations;
    if (cust?.home !== false) {
      setView("LandingWindow");
      if (setActiveIcon) setActiveIcon("home");
    } else if (cust?.ticket !== false) {
      setView("chatList");
      if (setActiveIcon) setActiveIcon("tickets");
    } else if (cust?.help) {
      setView("Help");
      if (setActiveIcon) setActiveIcon("help");
    } else if (cust?.news) {
      setView("News");
      if (setActiveIcon) setActiveIcon("news");
    } else if (cust?.roadmap) {
      setView("Roadmap");
      if (setActiveIcon) setActiveIcon("roadmap");
    } else {
      if (setIsOpen) setIsOpen(false);
    }
  };

  // Helper to extract last message details, sender name, profile picture, and relative time
  const getLastMessageInfo = (conv) => {
    const msgs = Array.isArray(conv.messages) ? conv.messages : [];
    let lastMsg = null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].type !== "Internal" && msgs[i].message) {
        lastMsg = msgs[i];
        break;
      }
    }

    const lastAgentMsg = [...msgs].reverse().find((m) => m.sender === "Agent" && m.senderId);
    const agentSenderId = lastAgentMsg?.senderId || (conv.userId ? String(conv.userId) : null);
    const agentProfile = agentSenderId
      ? agentProfiles[agentSenderId] || getCachedAgentInfo(agentSenderId)
      : null;

    const rawText = lastMsg?.message || conv.ticketId?.subject || "Start chatting";
    const cleanText = cleanTextSnippet(rawText) || "New message";

    const isAgent =
      conv.botHandled === "handed_off" ||
      conv.botHandled === false ||
      lastMsg?.sender === "Agent" ||
      Boolean(conv.user) ||
      Boolean(conv.lastInteractionAgentName) ||
      Boolean(agentSenderId);

    let displayName = botName;
    let agentAvatar = null;
    let agentInitials = "A";

    if (isAgent) {
      displayName =
        agentProfile?.name ||
        conv.user?.name ||
        conv.lastInteractionAgentName ||
        "Support Agent";

      agentAvatar =
        agentProfile?.avatar ||
        conv.user?.avatar ||
        conv.user?.profilePic ||
        null;

      agentInitials =
        agentProfile?.initials ||
        (displayName
          ? displayName
            .split(" ")
            .map((p) => p[0])
            .join("")
            .toUpperCase()
            .substring(0, 2)
          : "A");
    }

    const timestamp =
      lastMsg?.timestamp ||
      lastMsg?.createdAt ||
      conv.updatedAt ||
      conv.createdAt ||
      Date.now();
    const timeAgo = dayjs(timestamp).fromNow();

    return {
      cleanText,
      isAgent,
      displayName,
      timeAgo,
      agentAvatar,
      agentInitials,
    };
  };

  const cleanAndSanitizeMessage = (rawMessage) => {
    if (!rawMessage || typeof rawMessage !== "string") return "";

    let content = rawMessage.trim();
    content = content.replace(/<!--[\s\S]*?-->/g, "");
    content = content.replace(
      /<div[^>]*?(?:display:\s*none|font:\s*15px courier)[^>]*?>[\s\S]*?<\/div>/gi,
      ""
    );

    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);

    if (hasHtml) {
      // Clean up excessive whitespace/newlines between tags
      content = content.replace(/>\s*\n+\s*</g, "><");

      // Remove redundant wrapper div if it just sets font-family
      content = content.replace(
        /^<div style="font-family:[^>]*>([\s\S]*)<\/div>$/i,
        "$1"
      );

      // Sanitize with DOMPurify
      const sanitized = DOMPurify.sanitize(content, {
        ALLOWED_TAGS: [
          "b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span", "div",
          "code", "pre", "blockquote", "img"
        ],
        ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "style"],
      });

      // Strip trailing empty <p></p>, <br>, or &nbsp;
      return sanitized
        .replace(/(?:<p>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/p>|<br\s*\/?>|\s|&nbsp;)+$/gi, "")
        .trim();
    }

    // Plain text: convert newlines to <br> and sanitize
    const withBreaks = content.replace(/\n/g, "<br>");
    return DOMPurify.sanitize(withBreaks);
  };

  const handleFeedback = async (msgId, rating) => {
    if (!msgId) return;
    setFeedbackState((prev) => ({ ...prev, [msgId]: rating }));

    try {
      const chatUrl = process.env.REACT_APP_CHAT_MICROSERVICE_URL;
      const partnerId =
        widgetConfigs?.uniquePartnerId ||
        localStorage.getItem("uniquePartnerId") ||
        localStorage.getItem("partner_id");

      let storedCustomer = {};
      try {
        storedCustomer =
          JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details")) || {};
      } catch (e) { }

      const ticketNum =
        dexyConvo?.ticketId?.ticketNumber ||
        dexyConvo?.ticketNumber ||
        null;

      await axios.post(`${chatUrl}/conversations/message-csat`, {
        messageId: String(msgId),
        rating,
        roomId: dexyConvo?.roomId,
        conversationId: dexyConvo?._id,
        ticketNumber: ticketNum,
        ticketId: dexyConvo?.ticketId?._id || dexyConvo?.ticketId || undefined,
        uniquePartnerId: partnerId,
        customerId: storedCustomer?._id || customerDetails?._id || widgetConfigs?.userId,
        userEmail: storedCustomer?.email || customerDetails?.email || widgetConfigs?.userEmail || widgetConfigs?.email,
        userNumber: storedCustomer?.phone || customerDetails?.phone || widgetConfigs?.userNumber,
      });
    } catch (err) {
      console.warn("Message CSAT save note:", err.message);
    }
  };

  // Fetch existing message CSAT records for the active conversation room
  useEffect(() => {
    if (!dexyConvo?.roomId) return;
    const chatUrl = process.env.REACT_APP_CHAT_MICROSERVICE_URL;
    axios
      .get(`${chatUrl}/conversations/message-csat/${dexyConvo.roomId}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const stateMap = {};
          res.data.data.forEach((item) => {
            if (item.messageId && item.rating) {
              stateMap[item.messageId] = item.rating;
            }
          });
          setFeedbackState((prev) => ({ ...prev, ...stateMap }));
        }
      })
      .catch((err) => {
        console.log("Message CSAT fetch note:", err.message);
      });
  }, [dexyConvo?.roomId]);

  // Determine if active chat is handled by a human live support agent
  const isAgentHandling = useMemo(() => {
    if (handedOff) return true;
    if (dexyConvo?.botHandled === "handed_off" || dexyConvo?.botHandled === false) return true;
    if (dexyConvo?.userId || dexyConvo?.assignee) return true;
    if (messages.some((m) => m.sender === "Agent")) return true;
    return false;
  }, [handedOff, dexyConvo?.botHandled, dexyConvo?.userId, dexyConvo?.assignee, messages]);

  // Resolve active agent profile (real name, avatar, initials)
  const activeAgent = useMemo(() => {
    const lastAgentMsg = [...messages].reverse().find((m) => m.sender === "Agent" && m.senderId);
    if (lastAgentMsg?.senderId) {
      const id = String(lastAgentMsg.senderId);
      const profile = agentProfiles[id] || getCachedAgentInfo(id);
      if (profile) return profile;
    }

    const convAgent = dexyConvo?.userId || dexyConvo?.assignee;
    if (convAgent) {
      if (typeof convAgent === "object" && convAgent.name) {
        return {
          id: convAgent._id,
          name: convAgent.name,
          avatar: convAgent.profilePic || convAgent.avatar || null,
          initials: convAgent.name.charAt(0).toUpperCase(),
        };
      }
      const id = String(convAgent._id || convAgent);
      const profile = agentProfiles[id] || getCachedAgentInfo(id);
      if (profile) return profile;
    }

    const anyAgentMsg = [...messages].reverse().find((m) => m.sender === "Agent" && (m.fromAddress || m.senderName));
    if (anyAgentMsg) {
      const name = anyAgentMsg.senderName || anyAgentMsg.fromAddress || "Support Agent";
      return {
        id: anyAgentMsg.senderId || "agent",
        name,
        avatar: null,
        initials: name.charAt(0).toUpperCase(),
      };
    }

    if (availableAgents && availableAgents.length > 0) {
      return availableAgents[0];
    }

    return {
      name: "Support Agent",
      avatar: null,
      initials: "A",
    };
  }, [messages, dexyConvo?.userId, dexyConvo?.assignee, agentProfiles, availableAgents]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER SCREEN 1: PREVIOUS MESSAGES LIST VIEW (Gleap UI Screenshot)
  // ─────────────────────────────────────────────────────────────────────────────
  if (activeScreen === "list") {
    return (
      <div className="flex flex-col h-full flex-1 min-h-0 overflow-hidden bg-white relative">
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
          <button
            onClick={handleBackFromList}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Back"
          >
            <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
          </button>

          <div className="font-bold text-slate-900 text-base tracking-tight text-center">
            Messages
          </div>

          <button
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Conversation List Content */}
        <div className="flex-1 overflow-y-auto dexkor-scrollbar-hidden-chat bg-white pb-32">
          {loadingHistory ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 animate-pulse">
                  <div className="w-11 h-11 rounded-2xl bg-slate-200 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : customerConversations.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {customerConversations.map((conv, idx) => {
                const info = getLastMessageInfo(conv);
                const isUnread = Boolean(
                  (conv.roomId && unreadThreadIds.includes(conv.roomId)) ||
                  (conv._id && unreadThreadIds.includes(String(conv._id)))
                );
                return (
                  <div
                    key={conv._id || conv.roomId || idx}
                    onClick={() => handleSelectConversation(conv)}
                    className={`flex items-center px-4 py-3.5 hover:bg-slate-50/90 active:bg-slate-100 transition-colors cursor-pointer group ${isUnread ? "bg-red-50/40 border-l-[3.5px] border-l-[#ef4444]" : ""
                      }`}
                  >
                    {/* Avatar Icon */}
                    <div className="relative shrink-0">
                      {info.isAgent ? (
                        info.agentAvatar ? (
                          <img
                            src={info.agentAvatar}
                            alt={info.displayName}
                            className="w-8 h-8 rounded-2xl object-cover shrink-0 shadow-sm border border-slate-100"
                          />
                        ) : (
                          <div
                            className="w-8 h-8 rounded-2xl flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm"
                            style={{ backgroundColor: uiColor }}
                          >
                            {info.agentInitials}
                          </div>
                        )
                      ) : (
                        <div className="w-8 h-8 rounded-2xl bg-black flex items-center justify-center text-white shrink-0 shadow-sm">
                          <DexyBotIcon className="w-6 h-6" dotClass="w-1.5 h-1.5" gap="gap-[3px]" />
                        </div>
                      )}
                      {/* Red indicator dot if unread */}
                      {isUnread && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#ef4444] border-2 border-white rounded-full shadow-sm animate-pulse" />
                      )}
                    </div>

                    {/* Content Details */}
                    <div className="flex-1 min-w-0 ml-3.5 pr-1">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`text-[13.5px] truncate leading-snug ${isUnread ? "font-bold text-[#ef4444]" : "font-semibold text-slate-900 group-hover:text-blue-600 transition-colors"
                          }`}>
                          {info.cleanText}
                        </p>
                        {isUnread && (
                          <span className="shrink-0 text-[10px] font-bold text-white bg-[#ef4444] px-1.5 py-0.5 rounded-full shadow-sm uppercase tracking-wide">
                            New
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-slate-500 font-normal truncate mt-0.5 flex items-center">
                        <span className={`font-medium ${isUnread ? "text-[#ef4444]" : "text-slate-700"}`}>
                          {info.displayName}
                        </span>
                        <span className="mx-1.5 text-slate-300">•</span>
                        <span className={isUnread ? "text-red-500 font-medium" : ""}>{info.timeAgo}</span>
                      </div>
                    </div>

                    {/* Right Arrow */}
                    <ChevronRight className={`w-4 h-4 transition-all shrink-0 ${isUnread ? "text-red-500" : "text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5"
                      }`} />
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3 shadow-inner">
                <MessageSquare className="w-7 h-7 text-slate-500" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-1">No previous messages</h3>
              <p className="text-xs text-slate-500 max-w-[220px] leading-relaxed">
                Send us a message below to start chatting with Dexy AI or our live support team.
              </p>
            </div>
          )}
        </div>

        {/* Floating "Send us a message" Button */}
        <div className="absolute bottom-[72px] left-0 right-0 flex justify-center pointer-events-none z-30">
          <button
            onClick={handleStartNewChat}
            className="pointer-events-auto active:scale-95 text-white font-semibold text-sm px-6 py-2.5 rounded-full shadow-[0_4px_16px_rgba(0,0,0,0.18)] hover:shadow-[0_6px_22px_rgba(0,0,0,0.25)] hover:opacity-95 transition-all duration-200 flex items-center justify-center gap-2 border-none outline-none cursor-pointer"
            style={{
              backgroundColor: uiColor,
            }}
          >
            <span>Send us a message</span>
            <span className="text-sm font-bold">➔</span>
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER SCREEN 2: ACTIVE CHAT ROOM VIEW (Continuing or New Conversation)
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full flex-1 min-h-0 mb-0 mt-0 overflow-hidden bg-white">
      {/* Header - Adapts during session creation vs active bot chat vs live agent */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-start gap-2">



          <button
            onClick={handleBackFromChat}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Back to Messages"
          >
            <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
          </button>

          {initializing ? (
            /* While Session is creating for a few seconds: Show 3 overlapping active agents from auth microservice + Brand Name (Image 1 style) */
            <div className="flex items-center gap-2.5">
              <div className="flex items-center -space-x-2">
                {availableAgents && availableAgents.length > 0 ? (
                  availableAgents.slice(0, 3).map((ag, idx) =>
                    ag.avatar ? (
                      <img
                        key={ag.id || idx}
                        src={ag.avatar}
                        alt={ag.name}
                        title={`${ag.name} (Active)`}
                        className="w-8 h-8 rounded-full object-cover ring-2 ring-white shadow-sm"
                      />
                    ) : (
                      <div
                        key={ag.id || idx}
                        title={`${ag.name} (Active)`}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ring-2 ring-white shadow-sm"
                        style={{
                          backgroundColor: idx === 0 ? "#2563eb" : idx === 1 ? "#7c3aed" : "#059669",
                        }}
                      >
                        {ag.initials}
                      </div>
                    )
                  )
                ) : (
                  <div className="flex items-center -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse ring-2 ring-white" />
                    <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse ring-2 ring-white" />
                    <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse ring-2 ring-white" />
                  </div>
                )}
              </div>
              <span className="font-bold text-slate-900 text-sm ml-1">{brandName}</span>
            </div>
          ) : isAgentHandling ? (
            /* Live Agent Header with Real Agent Avatar & Name */
            <div className="flex items-center gap-2.5 text-left">
              <div className="relative">
                {activeAgent?.avatar ? (
                  <img
                    src={activeAgent.avatar}
                    alt={activeAgent.name || "Agent"}
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-100 shadow-sm"
                  />
                ) : (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                    style={{ backgroundColor: uiColor }}
                  >
                    {activeAgent?.initials || "A"}
                  </div>
                )}
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-slate-900 text-sm leading-tight">
                  {activeAgent?.name || "Support Agent"}
                </span>
                <span className="text-[11px] text-emerald-600 font-medium leading-tight mt-0.5 flex items-center gap-1">
                  Active now
                </span>
              </div>
            </div>
          ) : (
            /* Active AI Bot Header (Image 2 style: Bot Icon + Name + "Our bot will reply instantly") */
            <div className="flex items-center gap-2.5 text-left">
              <DexyBotIcon className="w-8 h-8" dotClass="w-1.5 h-1.5" gap="gap-[3px]" />
              <div className="flex flex-col">
                <span className="font-bold text-slate-900 text-sm leading-tight">{botName}</span>
                <span className="text-[11px] text-slate-400 font-normal leading-tight mt-0.5">
                  Our bot will reply instantly
                </span>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            if (setIsOpen) setIsOpen(false);
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Chat Area */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 dexkor-scrollbar-hidden-chat bg-white"
      >
        {initializing ? (
          /* While Session is creating for a few seconds: Dexy avatar + bouncing 3 dots (Image 1 style) */
          <div className="flex flex-col items-start mb-2 mt-1 animate-in fade-in duration-200">
            <div className="flex items-center gap-2 mb-1.5 ml-0.5">
              <DexyBotIcon className="w-5 h-5" dotClass="w-1 h-1" gap="gap-[2px]" />
              <span className="text-xs font-semibold text-slate-700">{botName}</span>
            </div>
            <div className="bg-[#f3f4f6] rounded-2xl rounded-tl-sm px-4 py-3 inline-flex items-center shadow-sm">
              <div className="flex space-x-1.5 py-0.5">
                <div className="w-2 h-2 rounded-full bg-slate-600 animate-bounce" />
                <div className="w-2 h-2 rounded-full bg-slate-600 animate-bounce [animation-delay:0.15s]" />
                <div className="w-2 h-2 rounded-full bg-slate-600 animate-bounce [animation-delay:0.3s]" />
              </div>
            </div>
          </div>
        ) : (
          /* Gleap Welcome Intro Bubble */
          <div className="flex flex-col items-start mb-2 mt-1 animate-in fade-in duration-200">
            <div className="flex items-center gap-2 mb-1.5 ml-0.5">
              <DexyBotIcon className="w-5 h-5" dotClass="w-1 h-1" gap="gap-[2px]" />
              <span className="text-xs font-semibold text-slate-700">{botName}</span>
            </div>

            <div
              className="border text-black p-3.5 rounded-2xl rounded-tl-sm shadow-sm text-sm max-w-[85%] leading-relaxed"
              style={{ background: "#f3f4f6", borderColor: "#e5e7eb" }}
            >
              Hey 👋! I'm here to assist you. How can I help you?
            </div>
            <span className="text-[10px] text-slate-400 font-medium ml-1 mt-1">
              a few seconds ago
            </span>
          </div>
        )}

        {/* Dynamic Messages */}
        {messages.map((msg, idx) => {
          if (msg.type === "Internal") return null;

          const isCustomer = msg.sender === "Customer";
          const isBot =
            msg.sender === "Chatbot" ||
            msg.sender === "Bot" ||
            msg.fromAddress === "Dexy AI" ||
            msg.fromAddress === botName ||
            msg.fromAddress === "Dexy";

          const msgId = msg._id || msg.clientMessageId || `msg_${idx}`;
          const isCurrentlyStreaming = streamingMessageId === msgId;
          const displayMessageText = isCurrentlyStreaming
            ? streamedTextMap[msgId] || msg.message
            : msg.message;

          return (
            <div
              key={idx}
              className={`flex ${isCustomer ? "justify-end ml-[15%]" : "justify-start mr-[15%]"} mb-3`}
            >
              <div
                className={`flex flex-col max-w-full ${isCustomer ? "items-end" : "items-start"}`}
              >
                {/* Bot or Real Agent Message Header (Avatar + Name) */}
                {!isCustomer && (
                  <div className="flex items-center gap-2 mb-1.5 ml-0.5">
                    {isBot ? (
                      <>
                        <DexyBotIcon className="w-5 h-5" dotClass="w-1 h-1" gap="gap-[2px]" />
                        <span className="text-xs font-semibold text-slate-700">{botName}</span>
                      </>
                    ) : (
                      (() => {
                        const agentInfo =
                          (msg.senderId && (agentProfiles[msg.senderId] || getCachedAgentInfo(msg.senderId))) ||
                          activeAgent;
                        return (
                          <>
                            {agentInfo?.avatar ? (
                              <img
                                src={agentInfo.avatar}
                                alt={agentInfo.name || "Agent"}
                                className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-200"
                              />
                            ) : (
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-xs"
                                style={{ backgroundColor: uiColor }}
                              >
                                {agentInfo?.initials || "A"}
                              </div>
                            )}
                            <span className="text-xs font-semibold text-slate-800">
                              {agentInfo?.name || "Support Agent"}
                            </span>
                          </>
                        );
                      })()
                    )}
                  </div>
                )}

                {/* Message Bubble */}
                <div
                  className={`p-3.5 rounded-2xl shadow-sm text-sm break-words max-w-full leading-relaxed ${isCustomer ? "text-white rounded-tr-none" : "text-black rounded-tl-none"
                    }`}
                  style={{
                    background: isCustomer
                      ? `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`
                      : "#f3f4f6",
                    border: isCustomer ? "none" : "1px solid #e5e7eb",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cleanAndSanitizeMessage(displayMessageText),
                    }}
                  />
                  {isCurrentlyStreaming && (
                    <span className="inline-block w-1.5 h-3.5 bg-slate-400 ml-1 animate-pulse align-middle" />
                  )}
                </div>

                {/* AI Badge Chip */}
                {!isCustomer && isBot && (
                  <div className="mt-1.5 ml-1 flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200/80 px-1.5 py-0.5 rounded leading-none">
                      AI
                    </span>
                    <span className="text-[11px] text-slate-400 font-medium">
                      {isCurrentlyStreaming ? "typing..." : dayjs(msg.timestamp || msg.createdAt).fromNow()}
                    </span>
                  </div>
                )}

                {/* Customer Message Timestamp */}
                {isCustomer && (
                  <div className="flex items-center gap-2 mb-0 px-1 mt-1">
                    <span className="text-[10px] font-medium text-gray-400">
                      {dayjs(msg.timestamp || msg.createdAt).format("hh:mm A")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* CSAT Prompt: Only below the last Dexy AI bot message, not when agent is handling */}
        {(() => {
          // Don't show when a human agent is handling the conversation
          if (isAgentHandling) return null;

          const lastBotMsg = [...messages].reverse().find(
            (m) =>
              m.type !== "Internal" &&
              (m.sender === "Chatbot" ||
                m.sender === "Bot" ||
                m.fromAddress === "Dexy AI" ||
                m.fromAddress === botName ||
                m.fromAddress === "Dexy")
          );

          if (!lastBotMsg || loading || streamingMessageId) return null;

          // If an agent replied after the last bot message, hide the CSAT
          const lastBotTs = new Date(lastBotMsg.timestamp || 0).getTime();
          const agentRepliedAfter = messages.some(
            (m) => m.sender === "Agent" && new Date(m.timestamp || 0).getTime() > lastBotTs
          );
          if (agentRepliedAfter) return null;

          const lastMsgId = lastBotMsg._id || lastBotMsg.clientMessageId || "last_bot_msg";
          const currentRating = feedbackState[lastMsgId];

          return (
            <div className="flex items-center gap-3 px-3 py-2 mt-1 mb-2 text-xs text-slate-600 bg-slate-50/90 border border-slate-100 rounded-xl w-fit animate-in fade-in duration-200">
              <span className="font-medium text-slate-600">Was this helpful?</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleFeedback(lastMsgId, "no")}
                  className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 transition-all cursor-pointer border ${currentRating === "no"
                      ? "bg-red-50 text-red-600 border-red-300 font-semibold shadow-xs"
                      : "bg-white text-slate-400 border-slate-200 hover:text-slate-600 hover:border-slate-300"
                    }`}
                  title="Not helpful"
                >
                  <span className="text-sm">👎</span>
                  {currentRating === "no" && (
                    <span className="text-[11px] font-medium text-red-600">No</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleFeedback(lastMsgId, "yes")}
                  className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1 transition-all cursor-pointer border ${currentRating === "yes"
                      ? "bg-emerald-50 text-emerald-600 border-emerald-300 font-semibold shadow-xs"
                      : "bg-white text-slate-400 border-slate-200 hover:text-slate-600 hover:border-slate-300"
                    }`}
                  title="Helpful"
                >
                  <span className="text-sm">👍</span>
                  {currentRating === "yes" && (
                    <span className="text-[11px] font-medium text-emerald-600">Yes</span>
                  )}
                </button>
              </div>
            </div>
          );
        })()}


        {/* Resolved / Closed Ticket CSAT: use the same FeedbackForm/Feedback components as Message.js */}
        {(() => {
          const ticketStatus = dexyConvo?.ticketId?.status?.toLowerCase();
          if (ticketStatus === "resolved") {
            return (
              <FeedbackForm
                selectedConversation={dexyConvo}
                setSelectedConversation={(updated) => setDexyConvo(updated)}
                setConversations={() => {}}
                setupDetails={setupDetails}
              />
            );
          }
          if (ticketStatus === "closed") {
            return (
              <Feedback
                selectedConversation={dexyConvo}
                setupDetails={setupDetails}
              />
            );
          }
          return null;
        })()}


        {/* AI Thinking & Loading Progress Bubble (Image 2 style: Thinking... -> Gathering information...) - ONLY for AI bot */}
        {loading && !isAgentHandling && (
          <div className="flex flex-col items-start mb-2 mr-[15%] animate-in fade-in duration-200">
            <div className="flex items-center gap-2 mb-1.5 ml-0.5">
              <DexyBotIcon className="w-5 h-5" dotClass="w-1 h-1" gap="gap-[2px]" />
              <span className="text-xs font-semibold text-slate-700">{botName}</span>
            </div>

            <div
              className="text-slate-800 text-sm px-4 py-2.5 rounded-2xl rounded-tl-sm shadow-sm inline-flex items-center border transition-all duration-300"
              style={{ background: "#f3f4f6", borderColor: "#e5e7eb" }}
            >
              <span>{thinkingStage === 0 ? "Thinking..." : "Gathering information..."}</span>
            </div>
          </div>
        )}

        {/* Invisible anchor to ensure robust scroll to bottom */}
        <div ref={messagesEndRef} className="h-0 w-full" />
      </div>

      {/* Input Composer Card Area (Image 2 style: rounded card with Paperclip, Mic, and ArrowUp Send) */}
      <div className="px-3.5 pb-20 pt-2 relative bg-white w-full">
        <div className="relative rounded-2xl border border-slate-200/90 hover:border-slate-300 focus-within:border-slate-400 bg-white w-full max-w-full mx-auto transition-all shadow-sm p-3">
          <input
            type="text"
            placeholder={
              initializing
                ? "Connecting session..."
                : isListening
                  ? "Listening... Speak now 🎙️"
                  : "Write a reply..."
            }
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={initializing}
            className="w-full pb-2 px-1 border-none text-xs text-slate-800 placeholder-slate-400 focus:outline-none bg-transparent"
          />

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            {/* Left Tools: Attachment and Voice Mic */}
            <div className="flex items-center gap-1.5 text-slate-400">
              <button
                type="button"
                className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer"
                title="Attach document"
                onClick={() => alert("Attachment upload is ready")}
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={toggleListening}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border-none outline-none cursor-pointer ${isListening
                    ? "bg-red-100 text-red-600 animate-pulse ring-2 ring-red-400"
                    : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  }`}
                title={isListening ? "Listening... click to stop" : "Speech to text"}
              >
                <Mic className="w-4 h-4" />
              </button>
            </div>

            {/* Right Action: Upward Arrow Send Button */}
            <button
              onClick={() => handleSendMessage()}
              disabled={initializing || !userInput.trim()}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border-none outline-none ${!initializing && userInput.trim()
                  ? "text-white hover:scale-105 active:scale-95 cursor-pointer shadow-sm"
                  : "bg-slate-100 text-slate-300 cursor-not-allowed"
                }`}
              style={
                !initializing && userInput.trim()
                  ? {
                    backgroundColor: uiColor,
                  }
                  : {}
              }
              title="Send reply"
            >
              <ArrowUp className="w-4 h-4 stroke-[2.5]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DexyAi;
